import {cloneDeep, get, set} from 'lodash';
import React, {useCallback, useEffect, useState} from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import {useDispatch, useSelector} from 'react-redux';
import {useToasts} from 'react-toast-notifications';
import {Button, Form} from 'semantic-ui-react';
import Featured from '../components/system/Featured';
import config from '../config/config';
import {Language} from '../const/enums/Language';
import BuildInitialSystem from '../const/initial/System';
import {HighlightItem, System} from '../const/interfaces/System';
import {User} from '../const/interfaces/User';
import {
  fetchSystemError,
  fetchSystemPending,
  fetchSystemSuccess,
} from '../redux/actions/system/SystemAction';
import AxiosHelper from '../util/AxiosHelper';
import {validateSystem} from '../util/ValidationHelper';

const FeaturedView = () => {
  const {addToast} = useToasts();
  const dispatch = useDispatch();

  const loggedInUser: User = useSelector((state) => state.loggedInUser).data;
  const system: System =
    useSelector((state) => state.system).data ||
    BuildInitialSystem((loggedInUser && loggedInUser._id) || '');

  const [contentSearchResults, setContentSearchResults] = useState([] as any);
  const [categorySearchResults, setCategorySearchResults] = useState([] as any);

  const [langStrings, setLangStrings] = useState({
    'featuredContent.title': Language.EN,
  });

  useEffect(() => {
    if (loggedInUser && loggedInUser.token) {
      (async () => {
        try {
          dispatch(fetchSystemPending());
          const axiosHelper = new AxiosHelper(loggedInUser.token);

          // Get system
          const result = await axiosHelper.get(`${config.api}/system`);
          if (!result) {
            dispatch(fetchSystemError('No system found'));
          }

          // Set highlight dropdowns
          if (result && result.data) {
            try {
              const content = await axiosHelper.get(`${config.api}/content`, {
                _id: {
                  $in: Object.keys(
                    result.data.featuredContent.content.highlights,
                  ).map((k) =>
                    result.data.featuredContent.content.highlights[k]
                      .contentId &&
                    result.data.featuredContent.content.highlights[k].contentId
                      .length
                      ? result.data.featuredContent.content.highlights[k]
                          .contentId
                      : null,
                  ),
                },
              });

              if (content && content.data.results) {
                setContentSearchResults(
                  content.data.results.map((item) => {
                    return {
                      key: `hero-${item.contentName}-0`,
                      value: item._id,
                      text: item.contentName,
                      // Hiding to test performance
                      // image: item.featureContent.thumbnailId
                      //   ? `${config.api}/file/${item.featureContent.thumbnailId}`
                      //   : item.primaryContent[item.contentType]?.mediaUrl,
                    };
                  }),
                );
              }

              if (result.data.featuredContent.content.highlights) {
                const highlights = cloneDeep(
                  result.data.featuredContent.content.highlights,
                );
                const categoryOptions: any = [];
                for (const key of Object.keys(highlights)) {
                  if (highlights[key].isCategory) {
                    if (highlights[key].categoryId) {
                      const res = await axiosHelper.get(
                        `${config.api}/channel/category`,
                        {
                          categoryId: highlights[key].categoryId,
                        },
                      );

                      if (res) {
                        categoryOptions.push({
                          _id: res.data.channelId,
                          key: `feature-${res.data.contentId}-${
                            res.data.categoryId
                          }-${Math.random() * 100000}`,
                          value: res.data._id,
                          text: res.data.categoryName,
                          // Hiding to test performance
                          // image: `${config.api}/file/${res.data.thumbnailId}`,
                        });
                      }
                    }
                  }
                }
                setCategorySearchResults(categoryOptions);
              }
            } catch (err) {
              console.log(err);
            }
          }

          dispatch(fetchSystemSuccess(result.data));
        } catch (e) {
          dispatch(fetchSystemError(e));
        }
      })();
    }
  }, [loggedInUser, dispatch]);

  const handleChange = useCallback(
    (field: string | string[], data: any) => {
      if (field && data) {
        const cloned = cloneDeep(system);

        if (!Array.isArray(field)) {
          field = [field];
        }

        if (!Array.isArray(data)) {
          data = [data];
        }

        for (let i = 0; i < field.length; i++) {
          set(cloned, field[i], data[i].value);
        }

        dispatch(fetchSystemSuccess(cloned));
      }
    },
    [dispatch, system],
  );

  const handleLangChange = (field: string, lang: Language) => {
    if (field && lang) {
      const cloned = cloneDeep(langStrings);
      set(cloned, field, lang);
      setLangStrings(cloned);
    }
  };

  const handleSubmit = async () => {
    try {
      const axiosHelper = new AxiosHelper(loggedInUser.token);

      // Update system
      const updatedSystem = (
        await axiosHelper.put(`${config.api}/system`, system)
      ).data;
      dispatch(fetchSystemSuccess(updatedSystem));

      // Add toast
      addToast(`Featured content updated successfully!`, {
        appearance: 'success',
        autoDismiss: true,
      });
    } catch (e) {
      dispatch(fetchSystemError(e));

      // Add toast
      addToast(`Feature content update failed. Try again later.`, {
        appearance: 'error',
        autoDismiss: true,
      });
    }
  };

  const handleAddItem = () => {
    const cloned = cloneDeep(system);

    const highlights: HighlightItem[] =
      cloned.featuredContent?.content?.highlights || [];

    highlights.push({isCategory: false});

    cloned.featuredContent.content = {
      ...cloned.featuredContent.content,
      highlights,
    };

    dispatch(fetchSystemSuccess(cloned));
  };

  const buildHighlights = () => {
    if (system.featuredContent?.content?.highlights) {
      let highlights: any[] = [];
      for (
        let i = 0;
        i < system.featuredContent.content.highlights.length;
        i++
      ) {
        highlights.push(
          <Featured
            key={`feature-${i}`}
            index={i}
            loggedInUser={loggedInUser}
            system={system}
            onChange={handleChange}
            results={contentSearchResults}
            setResults={setContentSearchResults}
            categoryResults={categorySearchResults}
            setCategoryResults={setCategorySearchResults}
          />,
        );
      }
      return highlights;
    }
  };

  return (
    <Form className={'Page PageEditor'} as={'div'}>
      <div className={'PageContent'}>
        <h1>Featured Content</h1>

        <Form.Field>
          <label className={'LangStringSelector'}>
            <span
              className={
                langStrings['featuredContent.title'] !== Language.EN
                  ? 'normal-type'
                  : ''
              }
              onClick={() =>
                handleLangChange('featuredContent.title', Language.EN)
              }
            >
              Playlist Title
            </span>
            <span className={'normal-type'}> | </span>
            <span
              className={
                langStrings['featuredContent.title'] !== Language.CY
                  ? 'normal-type'
                  : ''
              }
              onClick={() =>
                handleLangChange('featuredContent.title', Language.CY)
              }
            >
              Teitl Rhestr Chwarae
            </span>
          </label>
          {langStrings['featuredContent.title'] === Language.EN && (
            <Form.Input
              placeholder={'Enter a playlist title'}
              value={get(system, 'featuredContent.title.' + Language.EN) || ''}
              onChange={(e, data) =>
                handleChange(`featuredContent.title.${Language.EN}`, data)
              }
              error={!get(system, 'featuredContent.title.' + Language.EN)}
            />
          )}
          {langStrings['featuredContent.title'] === Language.CY && (
            <Form.Input
              placeholder={'Rhowch deitl'}
              value={get(system, 'featuredContent.title.' + Language.CY) || ''}
              onChange={(e, data) =>
                handleChange(`featuredContent.title.${Language.CY}`, data)
              }
              error={!get(system, 'featuredContent.title.' + Language.CY)}
            />
          )}
        </Form.Field>

        <h2 className={'alt-colour-type'}>Active Featured Content</h2>

        <Button className={'PageSidebarAction'} onClick={handleAddItem} primary>
          Add Featured Content Item
        </Button>

        {buildHighlights()}
      </div>

      <aside className={'PageSidebar'}>
        <h2>Publishing</h2>

        <Form.Field required>
          <label>Active</label>
          <Form.Checkbox
            toggle
            label={'Show playlist'}
            checked={get(system, 'featuredContent.active') || false}
            onChange={(e, data) =>
              handleChange(`featuredContent.active`, {
                value: !!data.checked,
              })
            }
          />
        </Form.Field>

        <div className={'PageSidebarActions'}>
          <Button
            disabled={validateSystem(system)}
            className={'PageSidebarAction'}
            onClick={handleSubmit}
            primary
          >
            Update Featured Content
          </Button>
        </div>
      </aside>
    </Form>
  );
};

export default FeaturedView;
